/**
 * Groov order token — minted at checkout.
 *
 * Every route back into the scan flow depends on this. The buyer's order is found by a token WE generate here
 * and attach to the cart, which Shopify then carries into the order's note_attributes. /pages/app-clip reads it
 * as ?token=, the account and order pages read it off the order, and the backend's consumer_orders#by_token
 * resolves the whole scan state from it.
 *
 * Without it a buyer has no way back to their scan, so this runs before checkout rather than after: an order
 * created without the attribute can never be given one.
 *
 * WHY AN INTERCEPTOR AND NOT AN ONCLICK
 * The previous Dawn theme put onclick="groovAccountlessCheckout(this)" directly on its checkout buttons. Impact
 * renders that button through a shared 'button' snippet used across the whole theme, so an inline handler would
 * mean editing a component every other form depends on. Intercepting the form submit reaches the same buttons
 * with no Liquid change, and mirrors how groov-app-clip-cart-token.js already hooks this theme's cart.
 *
 * SIGNED-IN BUYERS GO THROUGH THE BACKEND
 * The Dawn handler did two things; the first port of it kept only this one. The other half matters more: when the
 * buyer has a Groov session, the backend builds the Storefront cart instead, stamping `_PlayerID` on every line and
 * `buyerIdentity.email` on the cart.
 *
 * `_PlayerID` is how orders/create recognises the buyer. Without it the webhook falls through to its accountless
 * branch and find-or-creates a user from whatever address was typed at checkout — so a signed-in buyer who types a
 * second address silently gets a second account, and the order never appears on the one they are signed in to.
 * That is invisible at checkout and only shows up later as a missing order.
 *
 * A GUEST'S CART SAYS WHOSE IT IS
 * The backend cart carries `_groov_flow=consumer` (the backend sets it). A guest's cart is Shopify's own, so we
 * set the same attribute here. It is not decoration: the store is shared with other order sources, and the
 * backend's orders/create webhook IGNORES an order that has neither `_PlayerID` nor `_groov_flow` — it cannot
 * tell it from somebody else's. A guest order without it would be paid for, never recorded, and its
 * /pages/app-clip page would report "could not find your order".
 *
 * FAILS OPEN, twice. If the backend cart cannot be built we fall back to the plain Shopify checkout with the token
 * attached, and if the token cannot be minted or attached checkout proceeds anyway. A buyer who cannot pay is a
 * worse outcome than a buyer whose order needs reattaching by hand.
 *
 * A GIFT-CARD-ONLY CART IS NOT A SCAN ORDER
 * Shopify's native gift card (from 9 Nov 2026) is a cart line with `gift_card: true`. The Rise.ai card it replaces
 * is an ordinary product to Shopify (`gift_card: false`), told apart by its product type "Gift Card". When every
 * line is one or the other, there is nothing to make and nothing to scan: the cart is stamped `_groov_flow=gift` and no token,
 * goes straight to Shopify's checkout, the thank-you extension shows no scan block for that flow, and the backend's
 * orders/create webhook ignores the order. A mixed cart (a card beside a pair) is a normal consumer order.
 * Checked from /cart.js; if that read fails the cart is treated as a normal order, which is the safe direction.
 */
(() => {
  const STORAGE_KEY = 'groov_order_token';
  const ATTRIBUTE_KEY = '_groov_order_token';
  const FLOW_KEY = '_groov_flow';
  const FLOW_CONSUMER = 'consumer';
  const FLOW_GIFT = 'gift';
  const rootUrl = window.Shopify?.routes?.root || '/';

  const resumingForms = new WeakSet();

  const isCheckoutSubmission = (event) =>
    event.submitter?.matches('[name="checkout"], #checkout, #CartDrawer-Checkout') === true;

  const mintToken = () => {
    try {
      // randomUUID needs a secure context. Storefronts are HTTPS, but a theme preview over a proxy may not be,
      // so fall back rather than throwing on the path to payment.
      const token =
        window.crypto?.randomUUID?.() ??
        `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
      localStorage.setItem(STORAGE_KEY, token);
      return token;
    } catch (error) {
      return null;
    }
  };

  const updateCartAttributes = async (attributes) => {
    const response = await fetch(`${rootUrl}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    if (!response.ok) throw new Error('Unable to update the cart attributes');
  };

  const attachToCart = (token) => updateCartAttributes({ [ATTRIBUTE_KEY]: token, [FLOW_KEY]: FLOW_CONSUMER });

  // Shopify's native card says so itself; the Rise.ai card (until 9 Nov 2026) only by its product type.
  const GIFT_CARD_PRODUCT_TYPE = 'gift card';
  const isGiftCardLine = (item) =>
    item.gift_card === true || String(item.product_type ?? '').trim().toLowerCase() === GIFT_CARD_PRODUCT_TYPE;

  // Every line a gift card. False on an empty cart or when the cart cannot be read.
  const isGiftCardOnlyCart = async () => {
    try {
      const response = await fetch(`${rootUrl}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return false;
      const cart = await response.json();
      const items = Array.isArray(cart?.items) ? cart.items : [];
      return items.length > 0 && items.every(isGiftCardLine);
    } catch (error) {
      return false;
    }
  };

  // Returns a checkout URL when the backend built the cart for us, null when it didn't (signed out) or couldn't.
  const backendCheckoutUrl = async (token) => {
    if (!window.GroovBackend?.hasToken?.()) return null;
    const result = await window.GroovBackend.createCheckout({ groovOrderToken: token });
    return result?.checkout_url ?? null;
  };

  const resumeCheckout = (form, submitter) => {
    resumingForms.add(form);
    try {
      form.requestSubmit(submitter || undefined);
    } catch (error) {
      resumingForms.delete(form);
      form.submit();
    }
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !isCheckoutSubmission(event)) return;

      // Our own resubmission coming back around.
      if (resumingForms.has(form)) {
        resumingForms.delete(form);
        return;
      }

      event.preventDefault();
      const submitter = event.submitter;

      isGiftCardOnlyCart()
        .then((giftOnly) => {
          if (giftOnly) {
            // No token, no backend cart: mark the flow and let Shopify take the payment.
            return updateCartAttributes({ [FLOW_KEY]: FLOW_GIFT, [ATTRIBUTE_KEY]: '' })
              .catch(() => {})
              .finally(() => resumeCheckout(form, submitter));
          }
          return tokenisedCheckout(form, submitter);
        });
    },
    true
  );

  const tokenisedCheckout = (form, submitter) => {
      const token = mintToken();
      if (!token) return resumeCheckout(form, submitter); // storage unavailable — let checkout proceed untagged

      return backendCheckoutUrl(token)
        .catch((error) => {
          console.warn('[Groov] Backend checkout failed, falling back to Shopify checkout:', error);
          return null;
        })
        .then((checkoutUrl) => {
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
            return;
          }
          return attachToCart(token)
            .catch(() => {})
            .finally(() => resumeCheckout(form, submitter));
        });
  };
})();
